import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { ScrollView, View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { ProjectType } from "@/types/db/projects";
import { ClientType } from "@/types/db/clients";
import useToast from "@/hooks/useToast";
import useAuth from "@/hooks/useAuth";

import { ShiftForm, ShiftPayload } from "./types";
import { ShiftTimeInput } from "@/components/experimental/ShiftTimeInput";
import { EnumInput } from "@/components/ui/EnumInput";
import { PreviewSection, ClockInOptions } from "@/components/screens/shared";
import { ShiftStatusType, SHIFT_VENUE_TYPES, ShiftVenueType } from "@/types/db/shifts";
import { theme } from "@/constants/themes";
import Chip from "@/components/ui/Chip";
import { supabase } from "@/lib/supabase-client";
import { LookupInput, type LookupInputOption } from "@/components/ui/LookupInput";
import { MarketsInput } from "@/components/inputs/MarketsInput";
import { AbilitiesInput } from "@/components/inputs/AbilitiesInput";
import { RestrictionsInput } from "@/components/inputs/RestrictionsInput";
import { ClientInput } from "@/components/inputs/ClientInput";
import { ProjectInput } from "@/components/inputs/ProjectInput";
import { ContactInput } from "@/components/inputs/ContactInput";
import { projectService } from "@/services/projectService";

export { ShiftPayload } from "./types";
export type { ShiftForm } from "./types";

// Status options for dropdown
const STATUS_OPTIONS = [
  { text: "PENDING", color: "pending" },
  { text: "APPROVED", color: "complete" },
  { text: "ADVANCED", color: "complete" },
  { text: "CANCELED", color: "attention" },
  { text: "CANCELED - BILLABLE", color: "neutral" },
  { text: "COMPLETE", color: "todo" },
] as const;

function getShiftStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    PENDING: colors.pending.border,
    APPROVED: colors.complete.border,
    ADVANCED: colors.complete.border,
    CANCELED: colors.attention.border,
    "CANCELED - BILLABLE": colors.neutral.border,
    COMPLETE: colors.todo.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

export interface ShiftCreateFormProps {
  initialShift?: Partial<ShiftForm>;
  onSave?: (payload: ShiftPayload) => Promise<void> | void;
  onChange?: (payload: ShiftPayload) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onProjectChange?: (project: ProjectType | null) => void;
  onClientChange?: (client: ClientType | null) => void;
}

export interface ShiftCreateFormRef {
  handleSave: () => Promise<void>;
  handleReset: () => void;
  updateShiftName: (name: string) => void;
  updateShiftDescription: (description: string) => void;
  updateShiftStatus: (status: string) => void;
  openProjectModal: () => void;
}

// Export status options for use in header
export const SHIFT_STATUS_OPTIONS = STATUS_OPTIONS;
export { getShiftStatusColor };

export const ShiftCreateForm = forwardRef<ShiftCreateFormRef, ShiftCreateFormProps>(
  ({ initialShift, onSave, onChange, onDirtyChange, onProjectChange, onClientChange }, ref) => {
    const { colors } = useTheme();
    const { displayNames } = useAuth();
    const toast = useToast();
    const [endTimeError, setEndTimeError] = useState<string | undefined>();

    // Loaded project for inheritance (fetched when shift.project_id changes)
    const [loadedProject, setLoadedProject] = useState<ProjectType | null>(null);

    // Location market (from shift's own location or project's location)
    const [locationMarket, setLocationMarket] = useState<{ id: string; name: string; fips_code?: string } | null>(null);
    // Inherited markets from project
    const [projectMarkets, setProjectMarkets] = useState<{ id: string; name: string; fips_code?: string }[]>([]);

    // ---------- form state ----------
    const [shift, setShift] = useState<ShiftForm>({
      name: initialShift?.name ?? "",
      project_id: initialShift?.project_id ?? "",
      client_id: initialShift?.client_id ?? "",
      status: initialShift?.status ?? "PENDING",
      start_time: initialShift?.start_time ?? null,
      end_time: initialShift?.end_time ?? null,
      meta_data: initialShift?.meta_data ?? {},
    });

    useEffect(() => {
      onChange?.({ shift });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shift]);

    const baseline = useRef(JSON.stringify({ shift }));
    const dirty = useMemo(() => JSON.stringify({ shift }) !== baseline.current, [shift]);

    // Notify parent of dirty state changes
    useEffect(() => {
      onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    // Validate dates
    useEffect(() => {
      if (shift.start_time && shift.end_time) {
        const startDate = new Date(shift.start_time);
        const endDate = new Date(shift.end_time);
        if (endDate <= startDate) {
          setEndTimeError("End time must be after start time");
        } else {
          setEndTimeError(undefined);
        }
      } else {
        setEndTimeError(undefined);
      }
    }, [shift.start_time, shift.end_time]);

    // Fetch full project when project_id changes (for inheritance)
    useEffect(() => {
      let cancelled = false;
      const loadProject = async () => {
        if (!shift.project_id) {
          setLoadedProject(null);
          onProjectChange?.(null);
          return;
        }
        try {
          const project = await projectService.getProjectById(shift.project_id);
          if (cancelled) return;
          setLoadedProject(project);
          onProjectChange?.(project);
          // Auto-set client from project if not already set
          if (project?.client_id && !shift.client_id) {
            setShift((s) => ({ ...s, client_id: project.client_id! }));
            onClientChange?.(project.client as ClientType | null);
          }
        } catch (err) {
          console.error("Failed to load project:", err);
        }
      };
      loadProject();
      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shift.project_id]);

    // Load project's own markets (inherited)
    useEffect(() => {
      const loadProjectMarkets = async () => {
        const projectMarketsArray = (loadedProject?.meta_data as any)?.markets;
        if (!projectMarketsArray || projectMarketsArray.length === 0) {
          setProjectMarkets([]);
          return;
        }
        const { data: markets } = await supabase
          .schema("lists")
          .from("markets")
          .select("id, name, fips_code")
          .in("id", projectMarketsArray);
        setProjectMarkets(markets || []);
      };
      loadProjectMarkets();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(loadedProject?.meta_data as any)?.markets]);

    // Load location market (from shift's location if set, otherwise project's location)
    useEffect(() => {
      const loadLocationMarket = async () => {
        const locationId = shift.location_id || loadedProject?.location_id;
        if (!locationId) {
          setLocationMarket(null);
          return;
        }
        const { data: location } = await supabase
          .schema("lists")
          .from("locations")
          .select("market_id")
          .eq("id", locationId)
          .single();
        if (!location?.market_id) {
          setLocationMarket(null);
          return;
        }
        const { data: market } = await supabase
          .schema("lists")
          .from("markets")
          .select("id, name, fips_code")
          .eq("id", location.market_id)
          .maybeSingle();
        setLocationMarket(market || null);
      };
      loadLocationMarket();
    }, [shift.location_id, loadedProject?.location_id]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      handleSave,
      handleReset,
      updateShiftName: (name: string) => {
        setShift((s) => ({ ...s, name }));
      },
      updateShiftDescription: (description: string) => {
        setShift((s) => ({ ...s, description }));
      },
      updateShiftStatus: (status: string) => {
        setShift((s) => ({ ...s, status: status as ShiftStatusType }));
      },
      openProjectModal: () => {
        // Project selection is now inline via ProjectInput
      },
    }));

    // ---------- location options (fetched once on mount) ----------
    const [locationOptions, setLocationOptions] = useState<LookupInputOption[]>([]);

    useEffect(() => {
      let cancelled = false;
      const loadLocations = async () => {
        try {
          const { data, error } = await supabase
            .schema("lists")
            .from("locations")
            .select("id, name, street, city, state, zip")
            .order("name", { ascending: true });
          if (error) throw error;
          if (cancelled) return;
          setLocationOptions(
            (data ?? []).map((loc: any) => ({
              id: loc.id,
              name: loc.name,
              subtitle: [loc.street, loc.city, loc.state, loc.zip].filter(Boolean).join(", ") || undefined,
              icon: "map-marker" as const,
            })),
          );
        } catch (err) {
          console.error("Failed to load locations:", err);
        }
      };
      loadLocations();
      return () => {
        cancelled = true;
      };
    }, []);

    // ---------- helpers ----------
    const updateShift = (patch: Partial<ShiftForm>) => setShift((s) => ({ ...s, ...patch }));

    const handleLocationSelect = async (locationId: string | null) => {
      if (!locationId) {
        updateShift({ location_id: undefined, location: undefined });
        return;
      }
      try {
        const { data } = await supabase
          .schema("lists")
          .from("locations")
          .select("id, name, street, city, state, zip")
          .eq("id", locationId)
          .single();
        if (data) {
          updateShift({
            location_id: locationId,
            location: {
              id: data.id,
              name: data.name,
              address: { street: data.street, city: data.city, state: data.state, zip: data.zip },
            },
          });
        }
      } catch (err) {
        console.error("Failed to load location:", err);
      }
    };

    const getVenueType = (): ShiftVenueType | undefined => {
      if (shift.meta_data?.venue_type) return shift.meta_data.venue_type;
      if ((loadedProject as any)?.meta_data?.venue_type) {
        return (loadedProject as any).meta_data.venue_type;
      }
      return undefined;
    };

    const isVenueTypeFromProject = () => {
      return !shift.meta_data?.venue_type && !!(loadedProject as any)?.meta_data?.venue_type;
    };

    const getClockInOptions = (): Record<string, boolean> => {
      if (shift.meta_data?.clock_in_options) {
        return shift.meta_data.clock_in_options as Record<string, boolean>;
      }
      if ((loadedProject as any)?.meta_data?.clock_in_options) {
        return (loadedProject as any).meta_data.clock_in_options as Record<string, boolean>;
      }
      return {};
    };

    const isClockInOptionsFromProject = () =>
      !shift.meta_data?.clock_in_options && !!(loadedProject as any)?.meta_data?.clock_in_options;

    const handleSave = async () => {
      if (!shift.name?.trim()) {
        toast.showToast({
          title: "Missing Shift Name",
          message: "Please enter a name for this shift",
          toastType: "warning",
        });
        return;
      }
      if (!shift.status) {
        toast.showToast({
          title: "Missing Status",
          message: "Please select a status for this shift",
          toastType: "warning",
        });
        return;
      }
      if (!shift.start_time) {
        toast.showToast({
          title: "Missing Start Time",
          message: "Please select a start time for this shift",
          toastType: "warning",
        });
        return;
      }
      if (!shift.end_time) {
        toast.showToast({
          title: "Missing End Time",
          message: "Please select an end time for this shift",
          toastType: "warning",
        });
        return;
      }
      if (endTimeError) {
        toast.showToast({
          title: "Invalid End Time",
          message: endTimeError,
          toastType: "error",
        });
        return;
      }

      try {
        await onSave?.({ shift });
        baseline.current = JSON.stringify({ shift });
      } catch (e: any) {
        throw e;
      }
    };

    const handleReset = () => {
      const nextShift: ShiftForm = {
        name: initialShift?.name ?? "",
        project_id: initialShift?.project_id ?? "",
        client_id: initialShift?.client_id ?? "",
        status: initialShift?.status ?? "PENDING",
        start_time: initialShift?.start_time ?? null,
        end_time: initialShift?.end_time ?? null,
        meta_data: initialShift?.meta_data ?? {},
      };
      setShift(nextShift);
      setLoadedProject(null);
      onProjectChange?.(null);
      onClientChange?.(null);
      baseline.current = JSON.stringify({ shift: nextShift });
    };

    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={{ gap: 16 }}>
          {/* Schedule */}
          <ShiftTimeInput
            label="Schedule"
            startTime={shift.start_time ?? null}
            endTime={shift.end_time ?? null}
            editing
            enableHeatmap
            onChange={(values) => {
              updateShift({ start_time: values.startTime, end_time: values.endTime });
            }}
          />

          {/* Project Details */}
          <ThemedText type="defaultSemiBold">Project Details</ThemedText>
          <ClientInput
            value={shift.client_id || null}
            state="editing"
            label={displayNames?.clients?.name || "Client"}
            onChange={(clientId) => {
              updateShift({ client_id: clientId || "", project_id: "" });
              onClientChange?.(null);
            }}
          />
          <ProjectInput
            value={shift.project_id || null}
            clientId={shift.client_id || null}
            state="editing"
            label={displayNames?.projects?.name || "Project"}
            onChange={(projectId) => updateShift({ project_id: projectId || "" })}
          />

          {/* Location */}
          <LookupInput
            label="Location"
            value={shift.location_id || null}
            options={locationOptions}
            state={loadedProject ? "editing" : "idle"}
            icon="map-marker"
            placeholder={loadedProject?.location?.name || "Select a location"}
            info={!shift.location_id && loadedProject?.location ? "From Project" : undefined}
            onChange={handleLocationSelect}
          />

          {/* Venue Type */}
          <PreviewSection
            title="Venue Type"
            badge={
              isVenueTypeFromProject() ? (
                <Chip
                  text="From Project"
                  style={{ paddingHorizontal: 8, paddingVertical: 2, backgroundColor: colors.cyan?.background }}
                />
              ) : null
            }
          >
            <EnumInput
              options={[...SHIFT_VENUE_TYPES]}
              value={getVenueType()}
              placeholder="Unspecified"
              disabled={!loadedProject}
              onSelect={(value) => {
                if (!loadedProject) {
                  toast.showToast({
                    title: `Select ${displayNames?.projects?.name || "Project"} First`,
                    message: `Please select a ${displayNames?.projects?.name?.toLowerCase() || "project"} before setting venue type`,
                    toastType: "warning",
                  });
                  return;
                }
                updateShift({
                  meta_data: {
                    ...shift.meta_data,
                    venue_type: value as ShiftVenueType,
                  },
                });
              }}
            />
          </PreviewSection>

          {/* Clock-In Options */}
          <PreviewSection
            title="Clock-In Options"
            badge={
              isClockInOptionsFromProject() ? (
                <Chip
                  text="From Project"
                  style={{ paddingHorizontal: 8, paddingVertical: 2, backgroundColor: colors.cyan?.background }}
                />
              ) : null
            }
          >
            <ClockInOptions
              values={getClockInOptions()}
              editing={true}
              onChange={(newValues) => {
                updateShift({
                  meta_data: {
                    ...shift.meta_data,
                    clock_in_options: newValues,
                  },
                });
              }}
            />
          </PreviewSection>

          {/* Onsite Contact */}
          <PreviewSection title="Onsite Contact">
            <ContactInput
              value={shift.meta_data?.onsite_contact || null}
              clientId={shift.client_id || null}
              state={loadedProject ? "editing" : "idle"}
              label="Contact"
              onChange={(contactId) =>
                updateShift({
                  meta_data: {
                    ...shift.meta_data,
                    onsite_contact: contactId || undefined,
                  },
                })
              }
            />
          </PreviewSection>

          {/* Abilities */}
          <AbilitiesInput
            value={shift.shift_abilities || []}
            inherited={(loadedProject?.project_abilities || []).map((pa) => ({
              ability: pa.ability,
              sourceLabel: `From ${displayNames?.projects?.name || "Project"}`,
            }))}
            state={loadedProject ? "editing" : "idle"}
            onChange={(abilities) => updateShift({ shift_abilities: abilities })}
          />

          {/* Restrictions */}
          <RestrictionsInput
            value={shift.shift_restrictions || []}
            inherited={(loadedProject?.project_restrictions || []).map((pr) => ({
              restriction: pr.restriction,
              sourceLabel: `From ${displayNames?.projects?.name || "Project"}`,
            }))}
            state={loadedProject ? "editing" : "idle"}
            onChange={(restrictions) => updateShift({ shift_restrictions: restrictions })}
          />

          {/* Markets */}
          <MarketsInput
            value={shift.meta_data?.markets || []}
            inheritedMarket={
              locationMarket
                ? {
                    ...locationMarket,
                    label: shift.location_id ? "From Location" : `From ${displayNames?.projects?.name || "Project"}`,
                  }
                : null
            }
            inheritedMarkets={projectMarkets.map((m) => ({
              ...m,
              label: `From ${displayNames?.projects?.name || "Project"}`,
            }))}
            state={loadedProject ? "editing" : "idle"}
            onChange={(ids) => updateShift({ meta_data: { ...shift.meta_data, markets: ids } })}
          />
        </ScrollView>
      </View>
    );
  },
);

ShiftCreateForm.displayName = "ShiftCreateForm";

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
